import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import UrlParse from 'url-parse';
import { CSSTransition } from 'react-transition-group';
import dayjs from 'core/utils/dayjs';
import FullScreen from 'core/modules/FullScreen/FullScreen';
import { dySPA } from 'core/utils/analytics/dyAnalytics';
import {
  isChrome, isEdge, isSafari, isIOS,
} from 'core/utils/helpers/browser';
import globalDyHandler from 'core/utils/helpers/dyHandler';
import SessionStorageCache from 'core/utils/cache/sessionStorage';

import { getParamsFromHash } from 'core/utils/helpers/getParamsFromHash';
import { getEmailPopupCookie, setEmailPopupCookie } from 'core/utils/helpers/emailPopupCookie';
import { redirectToTargetLandingPage } from 'core/utils/helpers/redirectToTargetLandingPage';
import detectButton from 'core/utils/helpers/detectButton';
import { postEmailOptInUserChoice } from 'core/modules/Modals/EmailOptInSubscribeModal/actions';

import {
  selectOnboardingStepWeighting, selectToolbarConfig, selectPageName, selectBrandName,
} from 'core/selectors/app';
import { selectCookieDomain, selectIsHttps } from 'core/selectors/env';
import { selectUserIsIdentified } from 'core/selectors/user';
import { selectIsMobileMQ, selectIsMobileOrTabletMQ } from 'core/modules/MediaQueries/selectors';

import { MODAL_TYPE_ONBOARDING, MODAL_TYPE_INSTORE_HIW } from 'core/modules/OldModal/types';
import { EMAIL_OPT_IN_MODAL_USER_CHOICE_UNDECIDED }
  from 'core/modules/Modals/EmailOptInSubscribeModal/constants';

import Welcome from './components/Welcome/Welcome';
import Email from './components/Email/Email';
import MobileCongratulations from './components/MobileCongratulations/MobileCongratulations';
import Favorites from './components/Favorites/Favorites';
import Button from './components/Button/Button';
import CustomPlaceholder from './components/CustomPlaceholder/CustomPlaceholder';
import { triggerOnboardingScreenViewed, triggerOnboardingCloseClicked } from './actions';

import config from './configs';
import './Onboarding.scss';

const FORCE_COOKIE_NAME = 'showOnboarding';
const FORCE_PARAM_NAME = 'showOnboarding';
const DEFAULT_STEP_WEIGHTING = ['welcome', 'email', 'favorites', 'button', 'mobileCongratulations'];

const emailPopupCookie = getEmailPopupCookie();
const sessionStorageCache = new SessionStorageCache();

function Onboarding() {
  const location = useLocation();
  const dispatch = useDispatch();
  const cookieDomain = useSelector(selectCookieDomain);
  const isHttps = useSelector(selectIsHttps);
  const stepWeighting = useSelector(selectOnboardingStepWeighting);
  const toolbarConfig = useSelector(selectToolbarConfig);
  const isMobileMQ = useSelector(selectIsMobileMQ);
  const isMobileOrTabletMQ = useSelector(selectIsMobileOrTabletMQ);
  const pageName = useSelector(selectPageName);
  const [totalSteps, setTotalSteps] = useState(null);
  const [stepMap, setStepMap] = useState({});
  const [step, setStep] = useState(parseInt(getParamsFromHash(location.hash).step, 10) || 1);
  const [autoFinishMobile, setAutoFinishMobile] = useState(false);
  const [shouldShowButtonStep, setShouldShowButtonStep] = useState(undefined);
  const {
    chromeDownloadUrl, enableButtonMarketing, enableButtonMarketingChrome, enableButtonMarketingSafari,
    enableButtonMarketingIOS, mobileButtonIOSDownloadUrl,
  } = toolbarConfig || {};

  const isEligibleForChrome = (isChrome || isEdge) && enableButtonMarketingChrome && !!chromeDownloadUrl;
  const isEligibleForSafari = isSafari && enableButtonMarketingSafari && !isMobileOrTabletMQ;
  const isEligibleForIOS = isIOS && enableButtonMarketingIOS && isMobileMQ && !!mobileButtonIOSDownloadUrl;

  const { customStepWeighting, components: { Acquisition }, checkShouldShowAcquisitionStep } = config;
  const hasFCookieTrue = Cookies.get('f') === 'true';
  const userIsIdentified = useSelector(selectUserIsIdentified);
  const brandName = useSelector(selectBrandName);
  const shouldShowAcquisitionStep = checkShouldShowAcquisitionStep(hasFCookieTrue, userIsIdentified, brandName);

  useEffect(() => {
    detectButton.then((hasButton) => {
      setShouldShowButtonStep(
        (isEligibleForChrome || isEligibleForSafari || isEligibleForIOS) && !hasButton && enableButtonMarketing);
    });
  }, []);

  useEffect(() => {
    // CLPL-18737. This flag can be used in A/B testing (e.g., DY, Insider)
    sessionStorageCache.setItem('onboardingWasShownThisSession', true);
  }, []);

  const setOnboardingCookie = () => {
    if (!isMobileOrTabletMQ) {
      Cookies.set('showOnboardingCongratulations', '1', { path: '/', domain: cookieDomain, secure: isHttps });
    }
  };

  const finishAndRedirect = () => {
    if (autoFinishMobile) {
      setAutoFinishMobile(false);
    }
    setOnboardingCookie();
    redirectToTargetLandingPage({ force: true });
  };

  const goToNextStepOrFinish = () => {
    if (step === totalSteps) {
      finishAndRedirect();
    } else {
      setStep(step + 1);
    }
  };

  const handleClose = () => {
    dispatch(triggerOnboardingCloseClicked(step, totalSteps));
    setStepMap({});
  };

  const sendAnalyticsActionOnScreenView = () => {
    const stepName = stepMap[step];
    dispatch(triggerOnboardingScreenViewed(step, stepName, totalSteps, pageName));
  };

  const addApplicableStep = (steps, stepName) => {
    switch (stepName) {
      case 'welcome':
        steps.push('welcome');
        break;
      case 'acquisition':
        if (shouldShowAcquisitionStep) {
          steps.push('acquisition');
        }
        break;
      case 'email':
        if (!(emailPopupCookie && emailPopupCookie.emailGlobalOptOut === '0')) {
          steps.push('email');
        }
        break;
      case 'favorites':
        // united react only for now which always has favorites
        steps.push('favorites');
        break;
      case 'button':
        if (shouldShowButtonStep) {
          steps.push('button');
        }
        break;
      case 'mobileCongratulations':
        if (isMobileOrTabletMQ) {
          steps.push('mobileCongratulations');
        }
        break;
      default:
        if (stepName.includes('customPlaceholder')) {
          steps.push(stepName);
        }
        break;
    }

    return steps;
  };

  const autoFinishOnMobile = () => {
    if (!autoFinishMobile) {
      setAutoFinishMobile(true);
      setTimeout(() => {
        finishAndRedirect();
      }, 4500);
    }
  };

  const createStepMap = () => {
    const steps = [];
    const sWeighting = globalDyHandler.stepWeighting || customStepWeighting || stepWeighting || DEFAULT_STEP_WEIGHTING;

    sWeighting.forEach(stepName => addApplicableStep(steps, stepName));

    const sMap = {};

    steps.forEach((s, index) => {
      sMap[index + 1] = s;
    });

    return sMap;
  };

  const updateEmailPopUpCookie = (optedIn) => {
    emailPopupCookie.seenCount += 1;
    emailPopupCookie.emailGlobalOptOut = optedIn ? '0' : '1';
    emailPopupCookie.showPopup = false;
    emailPopupCookie.lastSeen = dayjs();
    emailPopupCookie.hasSeen = true;
    setEmailPopupCookie({ emailPopupCookie, cookieDomain, isHttps });
  };

  const handleCookieAndMetadataIfNoEmail = (sMap) => {
    if (!Object.values(sMap).includes('email')) {
      const isOptedIn = emailPopupCookie?.emailGlobalOptOut === '0';
      updateEmailPopUpCookie(isOptedIn);
      dispatch(postEmailOptInUserChoice(EMAIL_OPT_IN_MODAL_USER_CHOICE_UNDECIDED)); // updated metadata
    }
  };

  const setTotalStepsAndStepMap = () => {
    const sMap = createStepMap();

    // If there is no Email Step update metadata and email popup cookie here
    handleCookieAndMetadataIfNoEmail(sMap);

    setStepMap(sMap);
    setTotalSteps(Object.keys(sMap).length);
  };

  const removeForceCookieIfExists = () => Cookies.remove(FORCE_COOKIE_NAME, { path: '', domain: cookieDomain });

  // prevent redirects back to this onboarding experience or showing unexpected Overlay
  const removeRedundantModalHashParamIfExists = href => (
    href.split(new RegExp(`[#&]modalType=(${MODAL_TYPE_ONBOARDING}|${MODAL_TYPE_INSTORE_HIW})`))[0]
  );

  const removeForceParamIfExists = (href) => {
    const urlParseObj = new UrlParse(location.search, {}, true);
    const forceParamValue = urlParseObj.query[FORCE_PARAM_NAME];
    let sanitizedHref = href;
    if (forceParamValue) {
      sanitizedHref = sanitizedHref.replace(`${FORCE_PARAM_NAME}=${forceParamValue}`, '');
      if (Object.keys(urlParseObj.query).length === 1) { // remove '?' if this was only parameter
        sanitizedHref = sanitizedHref.replace('?', '');
      }
    }
    return sanitizedHref;
  };

  const wasUserRedirectedToXDot = () => !!Cookies.get('redirectedToXByOnboarding');

  const clearUserRedirectedToXDotCookie = () =>
    Cookies.remove('redirectedToXByOnboarding', { path: '', domain: cookieDomain });

  const createSanitizedTargetingLandingPageUrl = () => {
    let targetLandingPage = wasUserRedirectedToXDot() ?
      window.location.href.replace('://x.', '://') : window.location.href;

    // done with checking if user was redirected to xdot; can delete even before session end
    clearUserRedirectedToXDotCookie();
    targetLandingPage = removeForceParamIfExists(targetLandingPage);

    targetLandingPage = removeRedundantModalHashParamIfExists(targetLandingPage);

    return targetLandingPage;
  };

  /*
   This function used to save to sessionStorage the user's desired page in the case that user tries to refresh
   the page midway through experience or closes browser. On 1st reload of site, if we see overlay lightboxtype in URL,
   we will take them directly to the target landing page instead (and then clear this sessionStorage value)
   We also rely on another localStorage value, shouldRedirectToTargetLandingPage, which only gets switched to true
   after uses passes email opt in screen.

   (In other cases, if user refreshes on page Welcome or opt in, they do not get redirected).
   */
  const saveTargetLandingPage = () => {
    const targetLandingPage =
      sessionStorage.getItem('targetLandingPage') || createSanitizedTargetingLandingPageUrl();
    sessionStorage.setItem('targetLandingPage', targetLandingPage);
    sessionStorage.setItem('redirectIfURLContains', 'lightboxType=onboardingOverlay');
  };

  const stepsData = { stepMap, step, totalSteps };

  const renderStep = () => {
    const stepName = stepMap[step];
    const sequenceValue = `${step} of ${totalSteps}`;

    /**
     * first step doesnt need spa event only  next steps
     * since on page load we are showing welcome screen
     */
    if (stepName !== 'welcome') {
      dySPA.sendEvent();
    }

    return (
      <>
        {
          (stepName === 'welcome') &&
          <Welcome
            handleWelcomeDoneClick={goToNextStepOrFinish}
            sequenceValue={sequenceValue}
            stepsData={stepsData}
          />
        }
        {
          (stepName === 'acquisition') &&
          <Acquisition
            handleAcquisitionDoneClick={goToNextStepOrFinish}
            sequenceValue={sequenceValue}
            stepsData={stepsData}
          />
        }
        {
          (stepName === 'email') &&
          <Email
            updateEmailPopUpCookie={updateEmailPopUpCookie}
            sequenceValue={sequenceValue}
            goToNextStepOrFinish={goToNextStepOrFinish}
            stepsData={stepsData}
          />
        }
        {
          (stepName === 'favorites') &&
          <Favorites handleFavoritesDoneClick={goToNextStepOrFinish} stepsData={stepsData} />
        }
        {
          (stepName === 'button') &&
          <Button
            handleButtonDoneClick={goToNextStepOrFinish}
            sequenceValue={sequenceValue}
            stepsData={stepsData}
          />
        }
        {
          (stepName.includes('customPlaceholder')) &&
          <CustomPlaceholder
            handlePlaceholderDoneClick={goToNextStepOrFinish}
            stepsData={stepsData}
          />
        }
        {
          (stepName === 'mobileCongratulations') &&
          <>
            <MobileCongratulations handleStartShoppingClick={goToNextStepOrFinish} />
            {autoFinishOnMobile()}
          </>
        }
      </>
    );
  };

  removeForceCookieIfExists();
  saveTargetLandingPage();

  useEffect(() => {
    if (shouldShowButtonStep !== undefined) {
      setTotalStepsAndStepMap();
    }
  }, [shouldShowButtonStep]);

  useEffect(() => {
    if (stepMap[step]) {
      sendAnalyticsActionOnScreenView();
    }
  }, [stepMap[step]]);

  if (isEmpty(stepMap)) {
    return null;
  }

  return (
    <CSSTransition in appear timeout={isMobileMQ ? 2000 : 0} classNames={isMobileMQ ? 'mn_slideup' : ''}>
      <FullScreen dataTopLayer>
        <div className="mn_onboarding">
          <button className="mn_closeButton" aria-label="Close" onClick={handleClose} />
          {renderStep()}
        </div>
      </FullScreen>
    </CSSTransition>
  );
}

export default Onboarding;
